/**
 * RAVIN Sidekick — Versioned Storefront Widget Loader (v2.1.1 — Hybrid Theme-Driven Bridge + Paper Gang / Pulse Integration)
 * Target Theme: RAVIN Stores — Doorway Lab 16 Oct 2026 (Theme ID: 159006785708)
 * Active Integration Method in Doorway Lab: Theme Bridge (`snippets/ravin-chat-theme-bridge.liquid`)
 *
 * Audit & Storefront Integration Capabilities:
 * 1. Native RAVIN Paper Gang & RAVIN Pulse Integration (`ravin-paper-gang.js` + `ravin-pulse.js`):
 *    - Synchronously mounts BOTH `body > button.ravin-launcher-btn` and `body > a.ravin-vip-btn` as direct
 *      children of `document.body` immediately on boot (without waiting for network fetches) so `ravin-pulse.js`
 *      immediately marks `[data-ravin-pulse]` as `.is-ready` and `ravin-paper-gang.js` displays the Chat/VIP
 *      character inside Paper Gang.
 *    - Automatically hides the standalone floating launcher button whenever Paper Gang (`[data-rv-paper-gang]`)
 *      or RAVIN Pulse (`[data-ravin-pulse]` / `html.ravin-pulse-integrated`) is present on the page, while keeping
 *      `body > button.ravin-launcher-btn` active in the DOM so `launcher.click()` from Paper Gang -> Chat/VIP -> Chat
 *      opens the V2.1 chat window.
 *    - Never auto-opens `.ravin-widget-container.active` on initial page load (clears stale session open flags) so
 *      `.is-chat-open` never hides Paper Gang or the Chat/VIP character on load.
 * 2. Single Loading Path & Singleton Lock:
 *    - Enforces atomic global singleton lock (`window.__RAVIN_WIDGET_SINGLETON_LOCK__`,
 *      `window.__RAVIN_WIDGET_V2_PROOF_LOADED__`, `window.__RavinSidekickLoaded`) and DOM ID
 *      deduplication (`#ravin-launcher-btn`, `#ravin-vip-btn`, `#ravin-widget-container`).
 * 3. Complete Vision Glass Implementation (`glassBlurPx` & `surfaceOpacity`):
 *    - Applies `backdrop-filter: blur(<glassBlurPx>px) saturate(180%)`, `-webkit-backdrop-filter`,
 *      and translucent `rgba(r, g, b, <surfaceOpacity>/100)` surface styling directly to
 *      `#ravin-widget-container` in the parent storefront DOM as well as inside the widget iframe.
 * 4. Strict Allowlist SVG Sanitizer & XSS-Safe DOM Construction:
 *    - Parses custom SVG via `DOMParser('image/svg+xml')` and enforces a strict tag and attribute
 *      allowlist (`ALLOWED_SVG_TAGS`, `ALLOWED_SVG_ATTRS`).
 *    - Renders launcher labels (`launcherLabel`) and monogram text (`headerLogoText`) strictly via
 *      `document.createElement('span')` + `.textContent` (never `innerHTML`).
 * 5. Guaranteed Shopify Design Settings Delivery to React `ChatInterface`:
 *    - Delivers `#ravin-theme-chat-config` both via URL parameter (`&theme_payload=...`) for zero-latency
 *      frame-0 initialization and via multi-wave `RAVIN_WIDGET_THEME_UPDATE` `postMessage` bursts across
 *      the full async bundle boot window (`0ms` through `5000ms`), plus on `RAVIN_WIDGET_READY` and on open.
 */
(function () {
  if (window.__RAVIN_WIDGET_V2_PROOF_LOADED__) {
    return;
  }
  window.__RAVIN_WIDGET_SINGLETON_LOCK__ = 'v2.1.1';
  window.__RAVIN_WIDGET_V2_PROOF_LOADED__ = true;
  window.__RavinSidekickLoaded = true;
  window.__RAVIN_ACTIVE_INTEGRATION_MODE__ =
    window.__RAVIN_ACTIVE_INTEGRATION_MODE__ ||
    (document.getElementById('ravin-theme-chat-config') ? 'theme_bridge_v2' : 'app_embed_v2');

  var WIDGET_VERSION = '2.1.1';
  var DEFAULT_APP_ORIGIN = 'https://ravincs.ai.studio';

  var scriptEl = document.currentScript;
  var appOrigin = DEFAULT_APP_ORIGIN;
  if (scriptEl && scriptEl.src) {
    try {
      var parsedSrc = new URL(scriptEl.src);
      if (
        parsedSrc.hostname.indexOf('run.app') !== -1 ||
        parsedSrc.hostname.indexOf('ai.studio') !== -1 ||
        parsedSrc.hostname === 'localhost'
      ) {
        appOrigin = parsedSrc.origin;
      }
    } catch (e) {}
  }
  if (window.__RAVIN_SIDEKICK_APP_ORIGIN__) {
    appOrigin = window.__RAVIN_SIDEKICK_APP_ORIGIN__;
  }

  var IS_DEBUG = window.location.href.indexOf('ravin_widget_debug=1') !== -1;
  function debugLog(step, detail) {
    if (IS_DEBUG) {
      console.log('[RAVIN Loader v2.1.1 DEBUG][' + step + ']:', detail || '');
    }
  }

  // ---------------------------------------------------------------------------
  // 1. Canonical RAVIN 2026 Presets (Priority 2)
  // ---------------------------------------------------------------------------
  var RAVIN_PRESETS = {
    editorial_light: {
      brandPrimary: '#111411',
      brandAcidLime: '#D9F64E',
      brandSurface: '#F2F0E9',
      brandElevated: '#FFFFFF',
      brandBorder: '#D0D1C8',
      brandText: '#111411',
      brandMuted: '#596258',
      glassBlurPx: 0,
      surfaceOpacity: 100,
      launcherShape: 'pill_with_label',
      launcherSizePx: 56,
      launcherRadiusPx: 14,
      launcherBgColor: '#111411',
      launcherTextColor: '#F2F0E9',
      launcherBorderColor: '#2A2F2A',
      launcherIconType: 'ravin_monogram',
      desktopWidthPx: 400,
      desktopHeightPx: 640,
      windowRadiusPx: 18,
      mobileLayoutMode: 'bottom_sheet_85dvh',
      headerBgColor: '#111411',
      headerTextColor: '#F2F0E9',
      headerStatusDotColor: '#D9F64E',
      userBubbleBg: '#111411',
      userBubbleText: '#F2F0E9',
      botBubbleBg: '#FFFFFF',
      botBubbleText: '#111411',
      agentBubbleBg: '#EAECE2',
      bubbleRadiusPx: 14,
      composerBg: '#FFFFFF',
      composerSendBg: '#111411',
      composerSendIconColor: '#D9F64E',
      desktopPosition: 'bottom-left',
      autoFlipPositionRtl: true,
      stickyAtcExtraOffsetY: 68
    },
    ravin_dark: {
      brandPrimary: '#F2F0E9',
      brandAcidLime: '#D9F64E',
      brandSurface: '#0B0D0C',
      brandElevated: '#151916',
      brandBorder: '#262D28',
      brandText: '#F2F0E9',
      brandMuted: '#8E988F',
      glassBlurPx: 8,
      surfaceOpacity: 98,
      launcherShape: 'pill_with_label',
      launcherSizePx: 56,
      launcherRadiusPx: 14,
      launcherBgColor: '#D9F64E',
      launcherTextColor: '#0B0D0C',
      launcherBorderColor: '#D9F64E',
      launcherIconType: 'ravin_monogram',
      desktopWidthPx: 400,
      desktopHeightPx: 640,
      windowRadiusPx: 18,
      mobileLayoutMode: 'bottom_sheet_85dvh',
      headerBgColor: '#111512',
      headerTextColor: '#F2F0E9',
      headerStatusDotColor: '#D9F64E',
      userBubbleBg: '#D9F64E',
      userBubbleText: '#0B0D0C',
      botBubbleBg: '#151916',
      botBubbleText: '#F2F0E9',
      agentBubbleBg: '#1D241E',
      bubbleRadiusPx: 14,
      composerBg: '#111512',
      composerSendBg: '#D9F64E',
      composerSendIconColor: '#0B0D0C',
      desktopPosition: 'bottom-left',
      autoFlipPositionRtl: true,
      stickyAtcExtraOffsetY: 68
    },
    vision_glass: {
      brandPrimary: '#111411',
      brandAcidLime: '#D9F64E',
      brandSurface: '#F5F6F2',
      brandElevated: '#FFFFFF',
      brandBorder: '#D8DDD5',
      brandText: '#111411',
      brandMuted: '#525B53',
      glassBlurPx: 24,
      surfaceOpacity: 78,
      launcherShape: 'pill_with_label',
      launcherSizePx: 56,
      launcherRadiusPx: 20,
      launcherBgColor: '#111411',
      launcherTextColor: '#FFFFFF',
      launcherBorderColor: '#FFFFFF',
      launcherIconType: 'sparkle',
      desktopWidthPx: 408,
      desktopHeightPx: 650,
      windowRadiusPx: 24,
      mobileLayoutMode: 'bottom_sheet_85dvh',
      headerBgColor: '#111411',
      headerTextColor: '#FFFFFF',
      headerStatusDotColor: '#D9F64E',
      userBubbleBg: '#111411',
      userBubbleText: '#FFFFFF',
      botBubbleBg: '#FFFFFF',
      botBubbleText: '#111411',
      agentBubbleBg: '#F2FCE2',
      bubbleRadiusPx: 18,
      composerBg: '#FFFFFF',
      composerSendBg: '#111411',
      composerSendIconColor: '#D9F64E',
      desktopPosition: 'bottom-left',
      autoFlipPositionRtl: true,
      stickyAtcExtraOffsetY: 68
    }
  };

  // ---------------------------------------------------------------------------
  // 2. Security Helpers: Allowlist SVG Sanitizer, Text Sanitizer & RGBA Helper
  // ---------------------------------------------------------------------------
  var ALLOWED_SVG_TAGS = {
    svg: true,
    g: true,
    path: true,
    circle: true,
    rect: true,
    line: true,
    polyline: true,
    polygon: true,
    ellipse: true,
    defs: true,
    lineargradient: true,
    radialgradient: true,
    stop: true,
    clippath: true
  };

  var ALLOWED_SVG_ATTRS = {
    viewbox: true,
    xmlns: true,
    width: true,
    height: true,
    fill: true,
    stroke: true,
    'stroke-width': true,
    'stroke-linecap': true,
    'stroke-linejoin': true,
    'stroke-miterlimit': true,
    'stroke-dasharray': true,
    'stroke-dashoffset': true,
    opacity: true,
    'fill-opacity': true,
    'stroke-opacity': true,
    'fill-rule': true,
    'clip-rule': true,
    d: true,
    cx: true,
    cy: true,
    r: true,
    rx: true,
    ry: true,
    x: true,
    y: true,
    x1: true,
    y1: true,
    x2: true,
    y2: true,
    points: true,
    transform: true,
    id: true,
    offset: true,
    'stop-color': true,
    'stop-opacity': true,
    gradientunits: true,
    gradienttransform: true,
    'clip-path': true,
    focusable: true,
    'aria-hidden': true
  };

  function isSafeSvgAttrValue(attrNameLower, rawVal) {
    var val = String(rawVal || '').trim();
    if (!val) return true;
    if (/(javascript|data|vbscript)\s*:|expression\s*\(|<|>/i.test(val)) {
      return false;
    }
    if (/url\s*\(/i.test(val)) {
      return /^url\(\s*#[a-zA-Z0-9_-]+\s*\)$/.test(val);
    }
    if (attrNameLower === 'xmlns' && val !== 'http://www.w3.org/2000/svg') {
      return false;
    }
    return true;
  }

  function parseAndSanitizeSvgNode(rawSvg) {
    if (!rawSvg || typeof rawSvg !== 'string') return null;
    var trimmed = rawSvg.trim();
    if (trimmed.toLowerCase().indexOf('<svg') === -1) return null;
    if (typeof DOMParser === 'undefined') return null;

    try {
      var parser = new DOMParser();
      var doc = parser.parseFromString(trimmed, 'image/svg+xml');
      if (doc.querySelector('parsererror')) return null;
      var root = doc.documentElement;
      if (!root || root.tagName.toLowerCase() !== 'svg') return null;

      function cleanNode(el) {
        var attrs = [];
        for (var i = 0; i < el.attributes.length; i++) {
          attrs.push(el.attributes[i]);
        }
        for (var j = 0; j < attrs.length; j++) {
          var attr = attrs[j];
          var nameLower = attr.name.toLowerCase();
          if (
            !ALLOWED_SVG_ATTRS[nameLower] ||
            nameLower.indexOf('on') === 0 ||
            nameLower.indexOf('xlink') !== -1 ||
            !isSafeSvgAttrValue(nameLower, attr.value)
          ) {
            el.removeAttribute(attr.name);
          }
        }

        var children = [];
        for (var c = 0; c < el.childNodes.length; c++) {
          children.push(el.childNodes[c]);
        }
        for (var k = 0; k < children.length; k++) {
          var child = children[k];
          if (child.nodeType === 1) {
            var tagLower = child.tagName.toLowerCase();
            if (!ALLOWED_SVG_TAGS[tagLower]) {
              el.removeChild(child);
            } else {
              cleanNode(child);
            }
          } else {
            el.removeChild(child);
          }
        }
      }

      cleanNode(root);
      root.setAttribute('class', 'ravin-v2-svg');
      root.setAttribute('aria-hidden', 'true');
      root.setAttribute('focusable', 'false');
      return document.importNode(root, true);
    } catch (e) {
      return null;
    }
  }

  function sanitizePlainText(rawText, fallback, maxLen) {
    if (typeof rawText !== 'string') return fallback;
    var cleaned = rawText
      .replace(/<[^>]*>?/gm, '')
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .trim();
    if (!cleaned) return fallback;
    return cleaned.substring(0, maxLen || 64);
  }

  function sanitizeHostedImageUrl(rawUrl) {
    if (!rawUrl || typeof rawUrl !== 'string') return '';
    var trimmed = rawUrl.trim();
    if (!trimmed) return '';
    if (/^(https:\/\/|\/\/cdn\.shopify\.com\/)/i.test(trimmed) && !/[\s"'<>`]/.test(trimmed)) {
      return trimmed;
    }
    return '';
  }

  function hexToRgba(hex, opacityFraction) {
    var safeHex = String(hex || '#F2F0E9').trim();
    var alpha = Math.max(0, Math.min(1, Number(opacityFraction != null ? opacityFraction : 1)));
    var clean = safeHex.replace(/^#/, '');
    var r = 242,
      g = 240,
      b = 233;
    if (clean.length === 3) {
      r = parseInt(clean.charAt(0) + clean.charAt(0), 16);
      g = parseInt(clean.charAt(1) + clean.charAt(1), 16);
      b = parseInt(clean.charAt(2) + clean.charAt(2), 16);
    } else if (clean.length === 6) {
      r = parseInt(clean.substring(0, 2), 16);
      g = parseInt(clean.substring(2, 4), 16);
      b = parseInt(clean.substring(4, 6), 16);
    }
    if (isNaN(r) || isNaN(g) || isNaN(b)) return safeHex;
    return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha.toFixed(3) + ')';
  }

  // ---------------------------------------------------------------------------
  // 3. Theme Bridge JSON Parsing & Non-Destructive Token Resolution
  // ---------------------------------------------------------------------------
  var lastConfigRawText = '';

  function parseThemeConfigNode() {
    var node =
      document.getElementById('ravin-theme-chat-config') ||
      document.getElementById('ravin-chat-theme-bridge-payload');
    if (!node || !node.textContent) return null;
    try {
      lastConfigRawText = node.textContent;
      return JSON.parse(node.textContent);
    } catch (e) {
      console.warn('[RAVIN Widget v2.1.1] Failed to parse #ravin-theme-chat-config JSON:', e);
      return null;
    }
  }

  function resolveEffectiveTokens(bridgePayload, remoteFallback) {
    var presetId =
      bridgePayload && bridgePayload.activePreset && RAVIN_PRESETS[bridgePayload.activePreset]
        ? bridgePayload.activePreset
        : 'editorial_light';
    var base = RAVIN_PRESETS[presetId];
    var resolved = {};
    for (var k in base) {
      if (Object.prototype.hasOwnProperty.call(base, k)) {
        resolved[k] = base[k];
      }
    }

    if (remoteFallback && !bridgePayload) {
      if (remoteFallback.position) resolved.desktopPosition = remoteFallback.position;
      if (remoteFallback.launcherColor) resolved.launcherBgColor = remoteFallback.launcherColor;
    }

    var overrides = (bridgePayload && bridgePayload.customOverrides) || {};
    for (var ok in overrides) {
      if (
        Object.prototype.hasOwnProperty.call(overrides, ok) &&
        overrides[ok] !== undefined &&
        overrides[ok] !== null &&
        overrides[ok] !== ''
      ) {
        resolved[ok] = overrides[ok];
      }
    }

    var copy = (bridgePayload && bridgePayload.bilingualCopy) || {};
    var assets = (bridgePayload && bridgePayload.customAssets) || {};
    var locale =
      (bridgePayload && bridgePayload.locale) ||
      (document.documentElement && document.documentElement.lang) ||
      'en';
    var isAr = String(locale).toLowerCase().indexOf('ar') === 0;

    resolved.locale = isAr ? 'ar' : 'en';
    resolved.direction = isAr ? 'rtl' : 'ltr';
    resolved.launcherLabel = isAr
      ? sanitizePlainText(copy.launcherLabelAr, 'اسأل رافين', 32)
      : sanitizePlainText(copy.launcherLabelEn, 'ASK RAVIN', 32);
    resolved.headerLogoText = sanitizePlainText(assets.headerLogoText, 'R', 4);
    resolved.headerLogoImageUrl = sanitizeHostedImageUrl(assets.headerLogoImageUrl);
    resolved.launcherCustomSvgRaw = typeof assets.launcherCustomSvg === 'string' ? assets.launcherCustomSvg : '';
    resolved.fontHeadingEn = sanitizePlainText(copy.fontHeadingEn, 'Anton', 40);
    resolved.fontBodyAr = sanitizePlainText(copy.fontBodyAr, 'Noto Sans Arabic', 40);

    return resolved;
  }

  var activeThemeConfig = parseThemeConfigNode();
  var isDesignMode = Boolean(
    (window.Shopify && window.Shopify.designMode) ||
      (activeThemeConfig && activeThemeConfig.designMode)
  );

  // Always start closed on storefront page load so `.ravin-widget-container.active` and `.is-chat-open`
  // never hide RAVIN Paper Gang or the Chat/VIP character on initial render.
  var SESSION_OPEN_KEY = 'ravin_v2_editor_window_open';
  try {
    sessionStorage.removeItem(SESSION_OPEN_KEY);
  } catch (e) {}
  var isOpen = false;

  var iframeEl = null;
  var widgetContainer = null;
  var launcherBtn = null;
  var vipBtn = null;
  var styleEl = null;
  var widgetAuthToken = null;
  var widgetAuthExpiresAt = null;
  var verifiedCustomerSummary = null;
  var tokenRefreshTimer = null;
  var remoteConfig = null;
  var lastUrl = window.location.href;
  var themeAckReceived = false;
  var syncWaveTimers = [];

  function safeAppendToBody(element, callback) {
    if (document.body) {
      if (element.parentElement !== document.body) {
        document.body.appendChild(element);
      }
      if (typeof callback === 'function') callback();
    } else {
      setTimeout(function () {
        safeAppendToBody(element, callback);
      }, 30);
    }
  }

  // ---------------------------------------------------------------------------
  // 4. Storefront Context & Signed App Proxy Session + Token Refresh
  // ---------------------------------------------------------------------------
  function extractShopifyContext() {
    var context = {
      source: 'shopify_widget',
      channel: 'chat',
      bridgeVersion: WIDGET_VERSION,
      integrationMode: window.__RAVIN_ACTIVE_INTEGRATION_MODE__ || 'theme_bridge_v2',
      pageType: 'other',
      currentUrl: window.location.href,
      productHandle: '',
      productId: '',
      productTitle: '',
      variantId: '',
      selectedOptions: {},
      collectionHandle: '',
      collectionTitle: '',
      searchQuery: '',
      widgetAuthToken: widgetAuthToken || null,
      widgetAuthExpiresAt: widgetAuthExpiresAt || null,
      identityStatus: widgetAuthToken ? 'shopify_verified' : 'anonymous',
      customerSummary: verifiedCustomerSummary || null
    };

    try {
      var path = window.location.pathname;
      if (path === '/' || path === '/index' || path === '') {
        context.pageType = 'home';
      } else if (path.indexOf('/products/') !== -1) {
        context.pageType = 'product';
        var parts = path.split('/products/');
        if (parts.length > 1) {
          context.productHandle = parts[1].split('/')[0].split('?')[0];
        }
        var metaTitle = document.querySelector('meta[property="og:title"]');
        context.productTitle = metaTitle
          ? metaTitle.getAttribute('content')
          : document.title.split('–')[0].trim();
        if (window.meta && window.meta.page && window.meta.page.resourceId) {
          context.productId = String(window.meta.page.resourceId);
        }
        var params = new URLSearchParams(window.location.search);
        var urlVariant = params.get('variant');
        if (urlVariant) {
          context.variantId = urlVariant;
        } else if (
          window.ShopifyAnalytics &&
          window.ShopifyAnalytics.meta &&
          window.ShopifyAnalytics.meta.selectedVariantId
        ) {
          context.variantId = String(window.ShopifyAnalytics.meta.selectedVariantId);
        }
      } else if (path.indexOf('/collections/') !== -1) {
        context.pageType = 'collection';
        var partsColl = path.split('/collections/');
        if (partsColl.length > 1) {
          context.collectionHandle = partsColl[1].split('/')[0].split('?')[0];
        }
      } else if (path.indexOf('/cart') !== -1) {
        context.pageType = 'cart';
      } else if (path.indexOf('/search') !== -1) {
        context.pageType = 'search';
        var sParams = new URLSearchParams(window.location.search);
        context.searchQuery = sParams.get('q') || '';
      }
    } catch (e) {}

    return context;
  }

  function scheduleTokenRefresh(expiresInSeconds) {
    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
      tokenRefreshTimer = null;
    }
    var ttlSec = Number(expiresInSeconds || 900);
    var refreshDelayMs = Math.max(60, ttlSec - 120) * 1000;
    tokenRefreshTimer = setTimeout(function () {
      fetchSignedAppProxyToken(true);
    }, refreshDelayMs);
  }

  function fetchSignedAppProxyToken(isRefresh) {
    if (isDesignMode) return Promise.resolve(null);
    return fetch('/apps/ravin-chat/session?ts=' + Date.now(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (data) {
        if (data && data.authenticated && data.widgetAuthToken) {
          var ttlSec = Number(data.expiresInSeconds || 900);
          widgetAuthToken = data.widgetAuthToken;
          widgetAuthExpiresAt = Date.now() + ttlSec * 1000;
          verifiedCustomerSummary = data.customerSummary || null;
          scheduleTokenRefresh(ttlSec);

          if (iframeEl && iframeEl.contentWindow) {
            iframeEl.contentWindow.postMessage(
              {
                type: 'RAVIN_WIDGET_AUTH_REFRESH',
                widgetAuthToken: widgetAuthToken,
                expiresAt: widgetAuthExpiresAt,
                customerSummary: verifiedCustomerSummary
              },
              appOrigin
            );
            if (isRefresh || isOpen) {
              sendContextToIframe();
            }
          }
          return data.widgetAuthToken;
        } else {
          widgetAuthToken = null;
          widgetAuthExpiresAt = null;
          verifiedCustomerSummary = null;
          return null;
        }
      })
      .catch(function () {
        return null;
      });
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && widgetAuthExpiresAt) {
      if (Date.now() > widgetAuthExpiresAt - 120000) {
        fetchSignedAppProxyToken(true);
      }
    }
  });

  // ---------------------------------------------------------------------------
  // 5. Non-Destructive Coexistence Detection (Reamaze & Paper Gang / Pulse)
  // ---------------------------------------------------------------------------
  function detectCoexistingChatWidgets() {
    var reamazePresent = Boolean(
      document.getElementById('reamaze-widget') ||
        document.getElementById('reamaze-container') ||
        document.querySelector('[data-reamaze-embed], iframe[src*="reamaze"]')
    );
    var pulsePresent = Boolean(document.querySelector('[data-ravin-pulse]'));
    var paperGangPresent = Boolean(document.querySelector('[data-rv-paper-gang]'));
    window.__RAVIN_COEXISTING_WIDGETS__ = {
      reamazeDetected: reamazePresent,
      ravinPulseDetected: pulsePresent,
      ravinPaperGangDetected: paperGangPresent
    };
    return {
      reamazePresent: reamazePresent,
      pulseOrGangPresent: pulsePresent || paperGangPresent
    };
  }

  // ---------------------------------------------------------------------------
  // 6. XSS-Safe Launcher DOM Builder & Vision Glass Container Styling
  // ---------------------------------------------------------------------------
  function createDefaultSvgElement(iconType) {
    var raw =
      iconType === 'sparkle'
        ? '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z"/></svg>'
        : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>';
    return parseAndSanitizeSvgNode(raw);
  }

  function renderLauncherContentDom(btnEl, tokens) {
    while (btnEl.firstChild) {
      btnEl.removeChild(btnEl.firstChild);
    }

    var iconAssigned = false;
    if (tokens.launcherIconType === 'custom_svg' && tokens.launcherCustomSvgRaw) {
      var safeSvgNode = parseAndSanitizeSvgNode(tokens.launcherCustomSvgRaw);
      if (safeSvgNode) {
        var iconWrap = document.createElement('span');
        iconWrap.className = 'ravin-v2-icon';
        iconWrap.appendChild(safeSvgNode);
        btnEl.appendChild(iconWrap);
        iconAssigned = true;
      }
    }

    if (!iconAssigned) {
      if (tokens.launcherIconType === 'ravin_monogram') {
        var monoSpan = document.createElement('span');
        monoSpan.className = 'ravin-v2-monogram';
        monoSpan.textContent = String(tokens.headerLogoText || 'R').substring(0, 2);
        btnEl.appendChild(monoSpan);
      } else {
        var fallbackSvg = createDefaultSvgElement(
          tokens.launcherIconType === 'sparkle' ? 'sparkle' : 'chat_bubble'
        );
        if (fallbackSvg) {
          btnEl.appendChild(fallbackSvg);
        }
      }
    }

    if (
      tokens.launcherShape === 'pill_with_label' ||
      tokens.launcherShape === 'editorial_tag'
    ) {
      var labelSpan = document.createElement('span');
      labelSpan.className = 'ravin-v2-label';
      labelSpan.textContent = String(tokens.launcherLabel || 'ASK RAVIN');
      btnEl.appendChild(labelSpan);
    }
  }

  /**
   * Ensures both `body > a.ravin-vip-btn` and `body > button.ravin-launcher-btn` exist
   * as direct children of `document.body` so `ravin-pulse.js` and `ravin-paper-gang.js`
   * always detect `integrated = true` and render the Chat/VIP character inside Paper Gang.
   */
  function ensurePulseBridgeElements(tokens) {
    // 1. Direct `body > a.ravin-vip-btn` bridge node for `ravin-pulse.js`
    var existingVip = document.querySelector('body > a.ravin-vip-btn') || document.getElementById('ravin-vip-btn');
    if (existingVip && !vipBtn) {
      vipBtn = existingVip;
    }
    if (!vipBtn) {
      vipBtn = document.createElement('a');
      vipBtn.id = 'ravin-vip-btn';
      vipBtn.className = 'ravin-vip-btn';
      vipBtn.textContent = (remoteConfig && remoteConfig.vipBtnText) || '♡ VIP Club';
    }
    var vipUrl =
      (remoteConfig && remoteConfig.vipBtnUrl) || 'https://shop.iravin.com/pages/ravin-vip-club';
    var vipBehavior = (remoteConfig && remoteConfig.vipBtnOpenBehavior) || 'same_tab';
    vipBtn.setAttribute('href', vipUrl);
    if (vipBehavior === 'new_tab') {
      vipBtn.setAttribute('target', '_blank');
      vipBtn.setAttribute('rel', 'noopener noreferrer');
    } else {
      vipBtn.setAttribute('target', '_self');
      vipBtn.removeAttribute('rel');
    }
    safeAppendToBody(vipBtn);

    // 2. Direct `body > button.ravin-launcher-btn` bridge node for `ravin-pulse.js`
    var existingBtn =
      document.querySelector('body > button.ravin-launcher-btn') ||
      document.getElementById('ravin-launcher-btn');
    if (existingBtn && !launcherBtn) {
      launcherBtn = existingBtn;
    }
    if (!launcherBtn) {
      launcherBtn = document.createElement('button');
      launcherBtn.id = 'ravin-launcher-btn';
      launcherBtn.className = 'ravin-launcher-btn';
      launcherBtn.setAttribute('type', 'button');
      launcherBtn.setAttribute('aria-label', 'Chat with RAVIN Sidekick');
      launcherBtn.disabled = false;
      launcherBtn.addEventListener('click', toggleWidget);
    }
    launcherBtn.disabled = false;
    launcherBtn.setAttribute('dir', tokens.direction);
    renderLauncherContentDom(launcherBtn, tokens);
    safeAppendToBody(launcherBtn);
  }

  function renderOrUpdateLauncherAndWindow() {
    var tokens = resolveEffectiveTokens(activeThemeConfig, remoteConfig);
    var isRtl = tokens.direction === 'rtl';
    var baseCorner = tokens.desktopPosition || 'bottom-left';
    var effectiveCorner = baseCorner;
    if (isRtl && tokens.autoFlipPositionRtl !== false) {
      effectiveCorner = baseCorner === 'bottom-left' ? 'bottom-right' : 'bottom-left';
    }
    var isLeft = effectiveCorner === 'bottom-left';

    detectCoexistingChatWidgets();

    var hasStickyAtc = Boolean(
      document.querySelector(
        '[data-sticky-atc], .sticky-atc, .product-form__sticky, #sticky-atc-bar, [data-ravin-pdp-editorial] [data-sticky-cart]'
      )
    );
    var bottomOffsetPx = 24 + (hasStickyAtc ? Number(tokens.stickyAtcExtraOffsetY || 68) : 0);

    var radiusCss = '9999px';
    if (tokens.launcherShape === 'rounded_square') radiusCss = '16px';
    else if (tokens.launcherShape === 'editorial_tag') radiusCss = '4px';

    var sizePx = Math.max(48, Math.min(72, Number(tokens.launcherSizePx || 56)));
    var hasLabel =
      tokens.launcherShape === 'pill_with_label' || tokens.launcherShape === 'editorial_tag';

    var blurPx = Math.max(0, Math.min(40, Number(tokens.glassBlurPx != null ? tokens.glassBlurPx : 0)));
    var opacityPct = Math.max(
      40,
      Math.min(100, Number(tokens.surfaceOpacity != null ? tokens.surfaceOpacity : 100))
    );
    var containerSurfaceRgba = hexToRgba(tokens.brandSurface || '#F2F0E9', opacityPct / 100);
    var containerBorderRgba = hexToRgba(
      tokens.brandBorder || '#D8DDD5',
      blurPx > 0 ? 0.55 : 1
    );
    var launcherBgRgba =
      blurPx > 0 && opacityPct < 100
        ? hexToRgba(tokens.launcherBgColor || '#111411', Math.max(0.78, opacityPct / 100))
        : tokens.launcherBgColor;

    var mobileSheetCss = '';
    if (tokens.mobileLayoutMode === 'bottom_sheet_85dvh') {
      mobileSheetCss =
        'top: auto !important; bottom: 0 !important; left: 0 !important; right: 0 !important; width: 100vw !important; height: 85dvh !important; max-height: 85dvh !important; border-radius: 24px 24px 0 0 !important;';
    } else if (tokens.mobileLayoutMode === 'floating_card') {
      mobileSheetCss =
        'top: auto !important; bottom: 16px !important; left: 12px !important; right: 12px !important; width: calc(100vw - 24px) !important; height: 78dvh !important; border-radius: 20px !important;';
    } else {
      mobileSheetCss =
        'top: 0 !important; bottom: 0 !important; left: 0 !important; right: 0 !important; width: 100vw !important; height: 100dvh !important; max-height: 100dvh !important; border-radius: 0 !important;';
    }

    styleEl = document.getElementById('ravin-widget-v2-styles');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'ravin-widget-v2-styles';
      document.head.appendChild(styleEl);
    }

    styleEl.textContent =
      '.ravin-vip-btn {' +
      '  display: none !important;' +
      '  visibility: hidden !important;' +
      '  opacity: 0 !important;' +
      '  pointer-events: none !important;' +
      '}' +
      '.ravin-launcher-btn {' +
      '  position: fixed !important;' +
      '  bottom: calc(' + bottomOffsetPx + 'px + env(safe-area-inset-bottom)) !important;' +
      (isLeft
        ? '  left: 24px !important; right: auto !important;'
        : '  right: 24px !important; left: auto !important;') +
      '  height: ' + sizePx + 'px !important;' +
      (hasLabel
        ? '  padding: 0 20px !important; width: auto !important;'
        : '  width: ' + sizePx + 'px !important; padding: 0 !important;') +
      '  border-radius: ' + radiusCss + ' !important;' +
      '  background-color: ' + launcherBgRgba + ' !important;' +
      '  color: ' + tokens.launcherTextColor + ' !important;' +
      '  border: 1px solid ' + (tokens.launcherBorderColor || tokens.launcherBgColor) + ' !important;' +
      (blurPx > 0
        ? '  backdrop-filter: blur(' + Math.min(blurPx, 16) + 'px) saturate(160%) !important;' +
          '  -webkit-backdrop-filter: blur(' + Math.min(blurPx, 16) + 'px) saturate(160%) !important;'
        : '') +
      '  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.24) !important;' +
      '  cursor: pointer !important;' +
      '  display: flex !important;' +
      '  align-items: center !important;' +
      '  justify-content: center !important;' +
      '  gap: 10px !important;' +
      '  z-index: 2147483647 !important;' +
      '  font-family: Inter, -apple-system, BlinkMacSystemFont, sans-serif !important;' +
      '  font-weight: 700 !important;' +
      '  font-size: 12px !important;' +
      '  letter-spacing: 0.06em !important;' +
      '  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s !important;' +
      '}' +
      '.ravin-launcher-btn:hover { transform: translateY(-2px) scale(1.03) !important; }' +
      /* Hide standalone floating launcher whenever RAVIN Pulse or Paper Gang is present on the page */
      'html.ravin-pulse-integrated body > .ravin-launcher-btn,' +
      'html.ravin-pulse-integrated body > .ravin-vip-btn,' +
      'body:has([data-ravin-pulse]) > .ravin-launcher-btn,' +
      'body:has([data-rv-paper-gang]) > .ravin-launcher-btn {' +
      '  display: none !important;' +
      '  visibility: hidden !important;' +
      '  opacity: 0 !important;' +
      '  pointer-events: none !important;' +
      '}' +
      '.ravin-v2-svg, .ravin-v2-icon svg { width: ' + Math.round(sizePx * 0.39) + 'px !important; height: ' + Math.round(sizePx * 0.39) + 'px !important; display: block !important; }' +
      '.ravin-v2-monogram { font-size: ' + Math.round(sizePx * 0.32) + 'px !important; font-weight: 900 !important; font-style: italic !important; line-height: 1 !important; }' +
      '.ravin-widget-container {' +
      '  position: fixed !important;' +
      '  bottom: calc(' + bottomOffsetPx + 'px + env(safe-area-inset-bottom)) !important;' +
      (isLeft
        ? '  left: 24px !important; right: auto !important;'
        : '  right: 24px !important; left: auto !important;') +
      '  width: ' + Number(tokens.desktopWidthPx || 400) + 'px !important;' +
      '  height: ' + Number(tokens.desktopHeightPx || 640) + 'px !important;' +
      '  max-width: calc(100vw - 48px) !important;' +
      '  max-height: calc(100dvh - 80px) !important;' +
      '  border-radius: ' + Number(tokens.windowRadiusPx || 18) + 'px !important;' +
      '  background-color: ' + containerSurfaceRgba + ' !important;' +
      '  border: 1px solid ' + containerBorderRgba + ' !important;' +
      '  backdrop-filter: ' + (blurPx > 0 ? 'blur(' + blurPx + 'px) saturate(180%)' : 'none') + ' !important;' +
      '  -webkit-backdrop-filter: ' + (blurPx > 0 ? 'blur(' + blurPx + 'px) saturate(180%)' : 'none') + ' !important;' +
      '  overflow: hidden !important;' +
      '  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.28) !important;' +
      '  z-index: 2147483647 !important;' +
      '  display: none;' +
      '  opacity: 0;' +
      '  transform: translateY(12px) scale(0.98);' +
      '  transition: opacity 0.22s ease, transform 0.22s ease !important;' +
      '}' +
      '.ravin-widget-container.active {' +
      '  display: block !important;' +
      '  opacity: 1 !important;' +
      '  transform: translateY(0) scale(1) !important;' +
      '}' +
      '.ravin-widget-iframe {' +
      '  width: 100% !important;' +
      '  height: 100% !important;' +
      '  border: none !important;' +
      '  background: transparent !important;' +
      '  display: block !important;' +
      '}' +
      '@media (max-width: 767px) {' +
      '  .ravin-widget-container {' +
      mobileSheetCss +
      '  }' +
      '}';

    ensurePulseBridgeElements(tokens);

    if (isOpen) {
      ensureIframeMounted(tokens);
    }
  }

  function scheduleThemeSyncWaves() {
    for (var i = 0; i < syncWaveTimers.length; i++) {
      clearTimeout(syncWaveTimers[i]);
    }
    syncWaveTimers = [];

    // Multi-wave delivery across 0ms–5000ms guarantees that even if the React iframe bundle
    // takes 1–3 seconds to finish downloading/booting on cold cache, it receives the Shopify
    // Theme Settings as soon as its message listener attaches.
    var delays = [0, 180, 450, 900, 1600, 2800, 4500];
    for (var d = 0; d < delays.length; d++) {
      (function (delayMs) {
        var timerId = setTimeout(function () {
          var latestConfig = parseThemeConfigNode() || activeThemeConfig;
          if (latestConfig) {
            activeThemeConfig = latestConfig;
            pushThemeUpdateToIframe(latestConfig);
          }
          sendContextToIframe();
        }, delayMs);
        syncWaveTimers.push(timerId);
      })(delays[d]);
    }
  }

  function buildIframeSrc(tokens) {
    var query =
      '?theme_bridge=v2&lang=' +
      encodeURIComponent(tokens.locale || 'en') +
      '&parent_origin=' +
      encodeURIComponent(window.location.origin);
    if (isDesignMode) query += '&design_mode=1&sandbox=1';
    var latestConfig = parseThemeConfigNode() || activeThemeConfig;
    if (latestConfig) {
      try {
        query += '&theme_payload=' + encodeURIComponent(JSON.stringify(latestConfig));
      } catch (e) {}
    }
    return appOrigin + '/widget' + query;
  }

  function ensureIframeMounted(tokens) {
    var existingContainer = document.getElementById('ravin-widget-container');
    if (existingContainer && !widgetContainer) {
      widgetContainer = existingContainer;
      iframeEl = document.getElementById('ravin-widget-iframe');
    }

    if (!widgetContainer) {
      widgetContainer = document.createElement('div');
      widgetContainer.id = 'ravin-widget-container';
      widgetContainer.className = 'ravin-widget-container' + (isOpen ? ' active' : '');
      if (isOpen) widgetContainer.style.display = 'block';

      iframeEl = document.createElement('iframe');
      iframeEl.id = 'ravin-widget-iframe';
      iframeEl.className = 'ravin-widget-iframe';
      iframeEl.setAttribute('src', buildIframeSrc(tokens));
      iframeEl.setAttribute('title', 'RAVIN Sidekick Live Support');
      iframeEl.setAttribute('allow', 'camera; microphone; geolocation');

      iframeEl.addEventListener('load', function () {
        themeAckReceived = false;
        scheduleThemeSyncWaves();
      });

      widgetContainer.appendChild(iframeEl);
      safeAppendToBody(widgetContainer);
    } else if (document.body && widgetContainer.parentElement !== document.body) {
      safeAppendToBody(widgetContainer);
    }
  }

  function openWidget() {
    var tokens = resolveEffectiveTokens(activeThemeConfig, remoteConfig);
    ensureIframeMounted(tokens);
    isOpen = true;
    widgetContainer.style.display = 'block';
    widgetContainer.classList.add('active');
    scheduleThemeSyncWaves();
  }

  function closeWidget() {
    if (!widgetContainer) return;
    isOpen = false;
    widgetContainer.classList.remove('active');
    setTimeout(function () {
      if (!isOpen && widgetContainer) {
        widgetContainer.style.display = 'none';
      }
    }, 220);
  }

  function toggleWidget() {
    if (!isOpen) {
      openWidget();
    } else {
      closeWidget();
    }
  }

  // Close widget on Escape key press from parent storefront document
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isOpen) {
      closeWidget();
    }
  });

  function pushThemeUpdateToIframe(configPayload) {
    renderOrUpdateLauncherAndWindow();
    if (!iframeEl || !iframeEl.contentWindow || !configPayload) return;
    try {
      iframeEl.contentWindow.postMessage(
        {
          type: 'RAVIN_WIDGET_THEME_UPDATE',
          payload: configPayload
        },
        appOrigin
      );
    } catch (e) {}
  }

  function sendContextToIframe() {
    if (!iframeEl || !iframeEl.contentWindow) return;
    try {
      iframeEl.contentWindow.postMessage(
        {
          type: 'RAVIN_WIDGET_CONTEXT_SET',
          context: extractShopifyContext()
        },
        appOrigin
      );
    } catch (e) {}
  }

  function isTrustedWidgetAppOrigin(origin) {
    if (!origin) return false;
    if (origin === appOrigin) return true;
    return /^https:\/\/([a-zA-Z0-9.-]+\.ai\.studio|[a-zA-Z0-9-]+\.run\.app)$/.test(origin);
  }

  // Listen for handshake, close/minimize, and token refresh requests from `/widget?theme_bridge=v2`
  window.addEventListener('message', function (event) {
    if (!isTrustedWidgetAppOrigin(event.origin)) return;
    var data = event.data;
    if (!data || typeof data !== 'object') return;
    if (data.type === 'RAVIN_WIDGET_READY') {
      var freshConfig = parseThemeConfigNode() || activeThemeConfig;
      if (freshConfig) {
        activeThemeConfig = freshConfig;
        pushThemeUpdateToIframe(freshConfig);
      }
      sendContextToIframe();
    } else if (data.type === 'RAVIN_WIDGET_THEME_ACK') {
      themeAckReceived = true;
    } else if (data.type === 'RAVIN_WIDGET_REQUEST_TOKEN_REFRESH') {
      fetchSignedAppProxyToken(true);
    } else if (data.type === 'RAVIN_WIDGET_CLOSE' || data.type === 'RAVIN_WIDGET_MINIMIZE') {
      if (isOpen) closeWidget();
    }
  });

  // ---------------------------------------------------------------------------
  // 7. Resilient Shopify Theme Editor Synchronization & Paper Gang Self-Heal
  // ---------------------------------------------------------------------------
  function syncFromDomConfigIfChanged(forcePush) {
    var node =
      document.getElementById('ravin-theme-chat-config') ||
      document.getElementById('ravin-chat-theme-bridge-payload');
    if (!node || !node.textContent) return;
    if (forcePush || node.textContent !== lastConfigRawText) {
      var updated = parseThemeConfigNode();
      if (updated) {
        activeThemeConfig = updated;
        pushThemeUpdateToIframe(updated);
      }
    }
  }

  var shopifyEditorEvents = [
    'shopify:section:load',
    'shopify:section:select',
    'shopify:section:deselect',
    'shopify:block:select',
    'shopify:inspector:activate'
  ];
  for (var evIdx = 0; evIdx < shopifyEditorEvents.length; evIdx++) {
    document.addEventListener(shopifyEditorEvents[evIdx], function () {
      syncFromDomConfigIfChanged(true);
      nudgePulseIfUnready();
    });
  }

  function nudgePulseIfUnready() {
    if (!document.body) return;
    var tokens = resolveEffectiveTokens(activeThemeConfig, remoteConfig);
    ensurePulseBridgeElements(tokens);
    var pulseEl = document.querySelector('[data-ravin-pulse]');
    if (pulseEl && !pulseEl.classList.contains('is-ready')) {
      // Trigger `ravin-pulse.js` childList MutationObserver on `document.body`
      var marker = document.createElement('span');
      marker.hidden = true;
      marker.setAttribute('data-ravin-pulse-nudge', '1');
      document.body.appendChild(marker);
      document.body.removeChild(marker);
    }
  }

  function attachResilientConfigObserver() {
    if (typeof MutationObserver === 'undefined') return;
    var target = document.documentElement || document.body;
    if (!target) return;
    var observer = new MutationObserver(function () {
      syncFromDomConfigIfChanged(false);
      if (launcherBtn && document.body && launcherBtn.parentElement !== document.body) {
        safeAppendToBody(launcherBtn);
      }
      if (vipBtn && document.body && vipBtn.parentElement !== document.body) {
        safeAppendToBody(vipBtn);
      }
      if (widgetContainer && document.body && widgetContainer.parentElement !== document.body) {
        safeAppendToBody(widgetContainer);
      }
    });
    observer.observe(target, { childList: true, characterData: true, subtree: true });
  }

  // Reactive URL & Theme Editor config polling
  setInterval(function () {
    if (window.location.href !== lastUrl) {
      lastUrl = window.location.href;
      if (isOpen) {
        sendContextToIframe();
      }
    }
    if (isDesignMode) {
      syncFromDomConfigIfChanged(false);
    }
  }, 600);

  function bootLoader() {
    activeThemeConfig = parseThemeConfigNode();
    // Mount `body > button.ravin-launcher-btn` and `body > a.ravin-vip-btn` IMMEDIATELY
    // and synchronously on boot so `ravin-pulse.js` and `ravin-paper-gang.js` never miss them.
    renderOrUpdateLauncherAndWindow();
    attachResilientConfigObserver();
    setTimeout(nudgePulseIfUnready, 120);
    setTimeout(nudgePulseIfUnready, 600);

    fetchSignedAppProxyToken(false).then(function () {
      if (isOpen) sendContextToIframe();
    });

    // Fetch backend operational policy flags (`/api/widget-config`)
    fetch(appOrigin + '/api/widget-config?ts=' + Date.now(), { cache: 'no-store' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cfg) {
        remoteConfig = cfg;
        if (cfg && cfg.enabled === false && !isDesignMode && !activeThemeConfig) {
          debugLog('configDisabled', 'Widget disabled via operational kill-switch.');
          return;
        }
        renderOrUpdateLauncherAndWindow();
        nudgePulseIfUnready();
      })
      .catch(function () {
        renderOrUpdateLauncherAndWindow();
        nudgePulseIfUnready();
      });
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    bootLoader();
  } else {
    window.addEventListener('DOMContentLoaded', bootLoader);
  }

  window.__RAVIN_PUSH_THEME_UPDATE__ = pushThemeUpdateToIframe;
  window.__RAVIN_OPEN_WIDGET_V2__ = openWidget;
  window.__RAVIN_CLOSE_WIDGET_V2__ = closeWidget;
  window.__RAVIN_TOGGLE_WIDGET_V2__ = toggleWidget;
})();
